'use client';

import { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { Avatar, Box, Dialog, Label, Typography } from '@wishket/design-system';

import { AuthorStatistics } from './parts';
import { SubscribeButton, TagLineBox } from './parts/AuthorListCard.parts';

import { Link } from '@/shared/Components';
import { useDeviceCheck } from '@/shared/hooks';
import { PATH } from '@/shared/constants';
import { AuthorListCardProps } from '@/shared/types';

const AuthorListCard = ({
  id,
  name,
  imageUrl,
  isCompany,
  count,
  urlIdentifier,
  mostPublishedCategory,
  isSubscribed,
  description,
  tagLine,
  onSubscribe,
  isTooltipOpen,
}: AuthorListCardProps) => {
  const { isMobile } = useDeviceCheck();
  const [isOpen, setIsOpen] = useState(false);

  const handleSubscribe = () => {
    setIsOpen(false);
    onSubscribe?.(id);
  };

  return (
    <>
      <Box
        className={twJoin(
          'relative rounded-3xl border border-w-gray-200 bg-w-white',
          'desktop:hover:-translate-y-1 desktop:hover:border-primary desktop:hover:shadow-lg desktop:hover:transition-transform'
        )}
      >
        <Link
          href={`${PATH.HOME}/@${urlIdentifier}`}
          passHref={true}
          className="flex flex-col gap-1.5 p-5 tablet:px-8 tablet:pb-8 tablet:pt-6"
          data-testid="feature-author-card"
        >
          <Box className="flex w-full items-center gap-3 tablet:gap-6">
            <Avatar imgUrl={imageUrl} size={isMobile ? 'md' : 'lg'} />
            <Box className="flex w-full items-center justify-between gap-4 tablet:items-start">
              <Box className="flex w-[calc(100%_-_92px)] flex-col gap-1 tablet:w-[calc(100%_-_142px)]">
                <Box
                  className={twJoin(
                    'flex h-9 w-full items-center gap-2 tablet:gap-3'
                  )}
                >
                  <Typography
                    variant="body1"
                    className={twJoin(
                      `line-clamp-1 break-all font-medium text-w-gray-900 hover:underline tablet:typo-subTitle1`
                    )}
                  >
                    {name}
                  </Typography>
                  {isCompany && (
                    <Label.Root
                      type="filled"
                      size="sm"
                      className="h-fit w-[38px] shrink-0"
                    >
                      <Label.Text>기업</Label.Text>
                    </Label.Root>
                  )}
                </Box>
                <TagLineBox
                  tagLine={tagLine}
                  subscription={count.subscription}
                  className="hidden tablet:flex"
                />
              </Box>
            </Box>
          </Box>
          <Box className="flex w-full flex-col gap-4 tablet:gap-5 tablet:pl-[72px]">
            <Box className="flex flex-col gap-3">
              <Box className="flex flex-col gap-1.5">
                <TagLineBox
                  tagLine={tagLine}
                  subscription={count.subscription}
                  className="flex tablet:hidden"
                />
                <Typography
                  variant="body2"
                  className="line-clamp-1 break-all text-w-gray-500"
                >
                  {description}
                </Typography>
              </Box>
              <Box className="flex items-center gap-3">
                <AuthorStatistics type="view" count={count.view} />
                <AuthorStatistics type="like" count={count.like} />
                <AuthorStatistics type="scrap" count={count.scrap} />
              </Box>
            </Box>
            <Box className="h-[22px]" />
          </Box>
        </Link>
        <SubscribeButton
          isSubscribed={isSubscribed}
          isTooltipOpen={isTooltipOpen}
          handleSubscribe={handleSubscribe}
          openUnsubscribeDialog={() => setIsOpen(true)}
        />
        <Box
          className="absolute bottom-5 left-5 flex gap-3 tablet:bottom-[33px] tablet:left-[105px]"
          date-testid="author-card-chip-section"
        >
          {mostPublishedCategory.map((category) => (
            <Link
              href={`${PATH.LIST}/${category[1]}`}
              key={category[1]}
              className="shrink-0"
            >
              <Label.Root type="filled" size="md" variant="gray" canHover>
                <Label.Text>{category[0]}</Label.Text>
              </Label.Root>
            </Link>
          ))}
        </Box>
      </Box>
      <Dialog
        title="알림 취소"
        confirm="취소하기"
        variant="caution"
        description={
          '알림을 취소할까요?\n작가의 최신 콘텐츠를 이메일로 받을 수 없어요.'
        }
        isOpen={isOpen}
        onConfirm={handleSubscribe}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export default AuthorListCard;
